/*
 * K-Stack admin SPA — root router.
 *
 * Phase 1 minimum-viable scope:
 *   /                    redirect → /platform/
 *   /login               public login page
 *   /not-authorized      shown when authed but not a platform admin
 *   /oauth/callback      "Sign in with Apple / Google" return (NEXUS-586)
 *   /step-up             passkey / recovery-code step-up for admin power (NEXUS-586)
 *   /platform/*          @nexus/admin-ui pages (the full operator surface)
 *   *                    catch-all redirect → /platform/
 *
 * There are intentionally NO K-Stack-specific operator pages at this
 * stage. Everything an operator needs to run the new Nexus instance
 * lives inside <NexusAdminRoutes> (Apps, Users, Credentials, RateCards,
 * AuditLog, Databases, Email, AiHosts, ScheduledTasks, HealthDashboard,
 * BrokerCredentials, TranslatorStatus, Collab — 13 pages from
 * @nexus/admin-ui v0.17.0).
 *
 * Phase 4 (admin SPA cutover) can add K-Stack-specific pages here
 * (e.g. cross-app billing rollup, operator runbook links) without
 * touching @nexus/admin-ui.
 */

import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { ADMIN_MFA_EVENT, NexusAdminProvider, NexusAdminRoutes, useNexusAdmin } from '@nexus/admin-ui';

import { AuthProvider, useAuth } from './lib/auth';
import { getToken, refreshAccessToken } from './lib/apiClient';
import { GATEWAY_BASE } from './lib/config';
import Splash from './components/Splash';
import HandoffStepUp from './components/HandoffStepUp';
import { isHandoffSession } from './lib/handoff';

const Login = lazy(() => import('./pages/Login'));
const NotAuthorized = lazy(() => import('./components/NotAuthorized'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const StepUp = lazy(() => import('./pages/StepUp'));

/**
 * Gate component for /platform/*. Defers rendering until /auth/me has
 * settled, then either renders children or redirects.
 */
function RequirePlatformAdmin({ children }: { children: ReactElement }) {
  const { status, me } = useAuth();
  const location = useLocation();

  if (status === 'unknown') return <Splash />;
  if (status === 'guest') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (status === 'denied') return <NotAuthorized />;
  // NEXUS-586: platform-admin power needs a passkey step-up for this session — ask right
  // here with the shared "Confirm it's you" dialog (no separate page, no per-page prompts).
  if (status === 'stepup' && me?.adminMfa?.webHandoff && !me.adminMfa.enrolled) return <EnrolPasskeyCard />;
  // O1/O3 (Erwin 2026-10-29): a hand-off window confirms with a code approved by a passkey elsewhere.
  if (status === 'stepup' && (me?.adminMfa?.webHandoff || isHandoffSession(getToken()))) return <HandoffStepUpGate />;
  if (status === 'stepup') return <StepUpGate />;
  return children;
}

/**
 * A session handed off from Helm XR is never admin by itself (Erwin D2 2026-10-29): it needs the
 * passkey step-up, and this admin has no passkey yet — so admin is denied and we say how to enrol.
 */
function EnrolPasskeyCard() {
  const { me, signOut } = useAuth();
  return (
    <div className="kstack-login-bg d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div className="card shadow-sm" style={{ width: '26rem' }}>
        <div className="card-body p-4 text-center">
          <h1 className="h5 mb-2">Enrol a passkey first</h1>
          <p className="text-muted small">
            You are signed in{me?.email ? ` as ${me.email}` : ''} from Helm XR. Platform admin from a
            hand-off window needs a passkey, and this account has none yet.
          </p>
          <p className="text-muted small">
            Sign in to the admin console normally (password, Apple/Google), open
            Profile → Security and add a passkey. Then open the console from Helm XR again.
          </p>
          <button type="button" className="btn btn-link btn-sm text-muted" onClick={signOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

/**
 * A1: in a hand-off window the admin-ui's own passkey dialog cannot work (no FIDO2 in GeckoView),
 * so AdminShell switches it off and routes ADMIN_MFA_REQUIRED to a /auth/me re-check, which shows
 * HandoffStepUpGate. `passkeyHere` switches the dialog back on for an explicit in-window attempt.
 */
const HandoffPasskeyCtx = createContext<{ passkeyHere: boolean; setPasskeyHere: (v: boolean) => void }>({
  passkeyHere: false,
  setPasskeyHere: () => undefined,
});

/**
 * The admin step-up for a session handed off from Helm XR: GeckoView cannot use a passkey, so the
 * window gets a step-up code approved with a passkey in Helm on another device (O1) or in Chrome
 * on the Beam Pro (O3), and polls for the stepped-up token. The in-window passkey dialog stays as a
 * secondary option (it works when the same session is in a real browser).
 */
function HandoffStepUpGate() {
  const { requestStepUp } = useNexusAdmin();
  const { me, signOut, adoptToken, refresh } = useAuth();
  const { passkeyHere, setPasskeyHere } = useContext(HandoffPasskeyCtx);
  // "Use a passkey in this window": the shared dialog is switched on only for this (AdminShell), then
  // opened once it is available; when it closes either way, /auth/me is re-read.
  useEffect(() => {
    if (!passkeyHere || !requestStepUp) return;
    let live = true;
    void requestStepUp().finally(() => { if (live) { setPasskeyHere(false); void refresh(); } });
    return () => { live = false; };
  }, [passkeyHere, requestStepUp, setPasskeyHere, refresh]);
  return (
    <div className="kstack-login-bg d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div className="card shadow-sm" style={{ width: '26rem' }}>
        <div className="card-body p-4 text-center">
          <h1 className="h5 mb-2">Confirm it&apos;s you</h1>
          <p className="text-muted small">Platform admin{me?.email ? ` for ${me.email}` : ''} from a Helm XR window needs your passkey on another device.</p>
          <div className="d-grid gap-2">
            <HandoffStepUp onToken={adoptToken} />
            <button type="button" className="btn btn-link btn-sm" disabled={passkeyHere} onClick={() => setPasskeyHere(true)}>Use a passkey in this window instead</button>
            <button type="button" className="btn btn-link btn-sm text-muted" onClick={signOut}>Sign out</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Shown while the session's admin step-up is missing or expired: opens the shared dialog
 * once; if the admin closes it, a card lets them reopen it or sign out (no prompt loop).
 */
function StepUpGate() {
  const { requestStepUp } = useNexusAdmin();
  const { me, signOut } = useAuth();
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current || !requestStepUp) return;
    asked.current = true;
    void requestStepUp();
  }, [requestStepUp]);
  return (
    <div className="kstack-login-bg d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div className="card shadow-sm" style={{ width: '24rem' }}>
        <div className="card-body p-4 text-center">
          <h1 className="h5 mb-2">Confirm it&apos;s you</h1>
          <p className="text-muted small">Platform admin needs your passkey (or a recovery code){me?.email ? ` for ${me.email}` : ''}. It lasts for the rest of this session.</p>
          <div className="d-grid gap-2">
            <button type="button" className="btn btn-primary" onClick={() => void requestStepUp?.()}>Confirm with passkey</button>
            <button type="button" className="btn btn-link btn-sm text-muted" onClick={signOut}>Sign out</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Non-fetch callers (the Remote Access terminal WebSocket) announce ADMIN_MFA_REQUIRED with a
 * window event; route it to the same shared dialog the API calls use.
 */
function StepUpEventBridge({ onHandoff }: { onHandoff?: () => void }) {
  const { requestStepUp } = useNexusAdmin();
  useEffect(() => {
    const on = onHandoff ?? (requestStepUp ? () => { void requestStepUp(); } : null);
    if (!on) return;
    window.addEventListener(ADMIN_MFA_EVENT, on);
    return () => window.removeEventListener(ADMIN_MFA_EVENT, on);
  }, [requestStepUp, onHandoff]);
  return null;
}

/**
 * Inner shell — reads auth so the NexusAdminProvider can feed the admin-ui
 * layout the signed-in email + a sign-out handler (powers the top-right
 * profile menu / change-password). Must live INSIDE <AuthProvider>.
 */
function AdminShell() {
  const { me, signOut, adoptToken, refresh } = useAuth();
  // A1: a hand-off window gets the code step-up (HandoffStepUpGate), not the passkey dialog.
  const handoff = me?.adminMfa?.webHandoff === true || isHandoffSession(getToken());
  const [passkeyHere, setPasskeyHere] = useState(false);
  const dialogOn = !handoff || passkeyHere;
  const recheck = useCallback(() => { void refresh(); }, [refresh]);
  // NEXUS-586: ADMIN_MFA_REQUIRED from any admin-ui call opens the package's shared
  // "Confirm it's you" dialog (enabled by onAccessToken); the blocked call is retried
  // after the passkey, so pages never show a banner + "Loading…".
  const onAccessToken = useCallback((t: string) => { void adoptToken(t); }, [adoptToken]);
  // An expired access token is renewed with the refresh token; once the admin session
  // is over (max age), sign out so the guard shows the sign-in page.
  const refreshOrSignOut = useCallback(async () => {
    const t = await refreshAccessToken();
    if (!t) signOut();
    return t;
  }, [signOut]);
  return (
    <NexusAdminProvider
      apiBase={GATEWAY_BASE}
      getAuthToken={getToken}
      userEmail={me?.email ?? null}
      onSignOut={signOut}
      onAccessToken={onAccessToken}
      refreshAccessToken={refreshOrSignOut}
      stepUpDialog={dialogOn}
      onAdminMfaRequired={dialogOn ? undefined : recheck}
    >
      <HandoffPasskeyCtx.Provider value={{ passkeyHere, setPasskeyHere }}>
      <StepUpEventBridge onHandoff={dialogOn ? undefined : recheck} />
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/not-authorized" element={<NotAuthorized />} />
          {/* NEXUS-586: "Sign in with Apple / Google" return (tokens in the fragment). Must
              match auth.admin_console_redirect_uris on the gateway exactly. */}
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          {/* NEXUS-586 phase 3: passkey / recovery-code step-up for admin power. */}
          <Route path="/step-up" element={<StepUp />} />

          {/* Root → platform health dashboard.
              Note: must be `path="/"` not `index` — `<Route index>` is
              only valid inside a parent layout route. Used at the top
              of <Routes> it throws an invariant in @remix-run/router
              (~router.js:241 in the minified bundle) and the whole
              tree fails to mount → white screen. */}
          <Route path="/" element={<Navigate to="/platform/" replace />} />

          {/* Nexus platform-admin surface. */}
          <Route
            path="/platform/*"
            element={
              <RequirePlatformAdmin>
                <NexusAdminRoutes basePath="/platform" />
              </RequirePlatformAdmin>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/platform/" replace />} />
        </Routes>
      </Suspense>
      </HandoffPasskeyCtx.Provider>
    </NexusAdminProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AdminShell />
    </AuthProvider>
  );
}
